import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

/** Erro de transferência com o motivo e o que o usuário pode fazer a respeito. */
type TransferError = { message: string; hint?: string };

/**
 * Traduz a resposta da edge function para uma explicação acionável.
 * As mensagens vêm de supabase/functions/transfer-ticket/index.ts.
 */
function explainTransferFailure(status: number, message: string): TransferError {
  const text = message.toLowerCase();

  if (status === 401) {
    return {
      message: "Sua sessão expirou.",
      hint: "Entre na sua conta novamente e refaça a transferência.",
    };
  }
  if (text.includes("destinatário não encontrado")) {
    return {
      message: "Esse email ainda não tem conta na Tickethall.",
      hint: "A transferência só funciona entre contas da plataforma. Peça para a pessoa se cadastrar com exatamente esse email e tente de novo — ou confira se você não digitou errado.",
    };
  }
  if (text.includes("ingresso não encontrado")) {
    return {
      message: "Não localizamos este ingresso.",
      hint: "Recarregue a página de ingressos e tente novamente.",
    };
  }
  if (text.includes("não é o dono")) {
    return {
      message: "Este ingresso não está mais na sua conta.",
      hint: "Ele pode já ter sido transferido ou vendido. Recarregue a página para ver a situação atual.",
    };
  }
  if (text.includes("não permite transferência")) {
    return {
      message: "O produtor bloqueou transferências para este tipo de ingresso.",
      hint: "Não há o que fazer pelo site — fale com o produtor do evento.",
    };
  }
  if (text.includes("transferir para si mesmo")) {
    return {
      message: "Esse é o email da sua própria conta.",
      hint: "Informe o email de quem vai receber o ingresso.",
    };
  }
  if (text.includes("status:")) {
    const ticketStatus = text.match(/status:\s*([^)]+)\)/)?.[1]?.trim();
    const motivo =
      ticketStatus === "used" || ticketStatus === "checked_in"
        ? "Ele já foi usado no check-in."
        : ticketStatus === "cancelled"
          ? "Ele foi cancelado."
          : ticketStatus === "transferred"
            ? "Ele já foi transferido."
            : "Ele não está mais ativo.";
    return {
      message: `Este ingresso não pode mais ser transferido. ${motivo}`,
      hint: "Se acha que é engano, fale com o suporte.",
    };
  }
  return {
    message: message || "Não foi possível concluir a transferência.",
    hint: "Tente novamente em alguns instantes. Se persistir, fale com o suporte.",
  };
}

interface TransferTicketModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketId: string;
  eventTitle: string;
  tierName: string;
}

export function TransferTicketModal({
  open,
  onOpenChange,
  ticketId,
  eventTitle,
  tierName,
}: TransferTicketModalProps) {
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [failure, setFailure] = useState<TransferError | null>(null);
  const queryClient = useQueryClient();

  // O erro é sobre o email que foi tentado; some assim que o usuário mexe nos campos.
  useEffect(() => setFailure(null), [email, confirmEmail]);
  useEffect(() => {
    if (!open) setFailure(null);
  }, [open]);

  const transferMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("transfer-ticket", {
        body: { ticketId, recipientEmail: email.toLowerCase().trim() },
      });

      if (error) {
        // functions.invoke devolve um erro genérico em status não-2xx; a causa real
        // vem no corpo da resposta, acessível por error.context.
        const response = (error as { context?: Response }).context;
        const body = await response?.clone().json().catch(() => null);
        const raw =
          body?.error ??
          (typeof error === "object" && "message" in error ? String(error.message) : String(error));
        throw explainTransferFailure(response?.status ?? 0, raw);
      }

      if (data?.error) {
        throw explainTransferFailure(200, String(data.error));
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-tickets"] });
      toast({ title: "Ingresso transferido!", description: `Enviado para ${email}` });
      setEmail("");
      setConfirmEmail("");
      onOpenChange(false);
    },
    onError: (err: TransferError | Error) => {
      // Fica no modal, ancorado ao campo que causou o problema — não em um toast que some.
      setFailure(
        "hint" in err
          ? err
          : { message: err.message, hint: "Tente novamente. Se persistir, fale com o suporte." },
      );
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl border-border bg-card p-6 sm:p-8">
        <DialogHeader className="space-y-3">
          <DialogTitle className="font-display text-xl font-semibold text-foreground">
            Transferir Ingresso
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
            Transfira seu ingresso <span className="font-medium text-foreground">{tierName}</span> para <span className="font-medium text-foreground">{eventTitle}</span> para outra pessoa.
            Esta ação é irreversível — o QR code atual será invalidado.
            O destinatário precisa ter uma conta na Tickethall com o email informado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          <div className="space-y-2">
            <Label htmlFor="recipientEmail" className="text-xs font-medium text-muted-foreground">
              Email do destinatário
            </Label>
            <Input
              id="recipientEmail"
              type="email"
              placeholder="email@exemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl h-12 border-border bg-muted/5 focus-visible:ring-primary/20"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmEmail" className="text-xs font-medium text-muted-foreground">
              Confirme o email
            </Label>
            <Input
              id="confirmEmail"
              type="email"
              placeholder="email@exemplo.com"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              className="rounded-xl h-12 border-border bg-muted/5 focus-visible:ring-primary/20"
            />
          </div>
          {email && confirmEmail && email !== confirmEmail && (
            <p className="text-xs text-destructive font-medium">Os e-mails informados não coincidem.</p>
          )}

          {failure && (
            <div
              role="alert"
              className="flex gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-4"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-destructive">{failure.message}</p>
                {failure.hint && (
                  <p className="text-xs leading-relaxed text-destructive/80">{failure.hint}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button 
            variant="ghost" 
            onClick={() => onOpenChange(false)}
            className="rounded-xl h-12 font-medium"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => transferMutation.mutate()}
            disabled={!email || email !== confirmEmail || transferMutation.isPending}
            className="rounded-xl h-12 font-semibold px-8"
          >
            {transferMutation.isPending ? "Processando..." : "Confirmar Transferência"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
